import { memo } from 'react';

import Conversion from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import ConversionDate from 'src/components/Conversion/DateOnly';
import PriceCartItemHH, { PriceCartItemHHProps } from 'src/components/Price/PriceCart/PriceCartItemHH';
import translation from 'src/components/translation';
import { PriceCartProductType } from 'src/models/price/priceCart';
import { BillingPlatform, DBAccessProductType } from 'src/models/price/product.types';
import { getContactsCountForPlatform, getPublicationsCount } from 'src/utils/price/products';

import Title from 'src/components/Price/PriceCart/PriceCartProduct/DbAccess/Title';

const formatPeriod = (value: number) => (
    <span suppressHydrationWarning data-qa="cart-item__period">
        {value}
    </span>
);

const TrlKeys = {
    contactsOne: 'price.cart.resume-access.civ.one',
    contactsSome: 'price.cart.resume-access.civ.some',
    contactsMany: 'price.cart.resume-access.civ.many',
    publicationsOne: 'price.cart.resume-access.publications.one',
    publicationsSome: 'price.cart.resume-access.publications.some',
    publicationsMany: 'price.cart.resume-access.publications.many',
    allProfRoleGroup: 'billing_professional_role_group.0',
};

interface DbAccessProps {
    product: PriceCartProductType<DBAccessProductType>;
}

const DbAccess: TranslatedComponent<DbAccessProps> = ({ trls, product }) => {
    const contactsCountHH = getContactsCountForPlatform(product, BillingPlatform.HH);

    const vpCount = getPublicationsCount(product) || 0;
    const list = [
        {
            key: 'date',
            element: (
                <ConversionDate
                    formatValue={formatPeriod}
                    value={parseInt(product.period, 10)}
                    shouldShowInHours={Number(product.period) === 1}
                />
            ),
        },
        contactsCountHH && {
            key: 'civ',
            element: (
                <span data-qa="cart-item__count">
                    <Conversion
                        value={contactsCountHH}
                        one={trls[TrlKeys.contactsOne]}
                        some={trls[TrlKeys.contactsSome]}
                        many={trls[TrlKeys.contactsMany]}
                    />
                </span>
            ),
        },
        vpCount && {
            key: 'publications',
            element: (
                <span data-qa="cart-item__publications-count">
                    <Conversion
                        value={vpCount}
                        one={trls[TrlKeys.publicationsOne]}
                        some={trls[TrlKeys.publicationsSome]}
                        many={trls[TrlKeys.publicationsMany]}
                    />
                </span>
            ),
        },
        {
            key: 'profRole',
            element: (
                <span data-qa="cart-item__profRoleGroup">
                    {product.profRoleGroupTrls
                        ? product.profRoleGroupTrls.map((profRoleGroupTrl, index, profRoleGroups) => (
                              <p key={profRoleGroupTrl} data-qa="cart-item__prof-role-group">
                                  {profRoleGroupTrl}
                                  {index !== profRoleGroups.length - 1 && ';'}
                              </p>
                          ))
                        : trls[TrlKeys.allProfRoleGroup]}
                </span>
            ),
        },
        product.regionTrl && {
            key: 'region',
            element: <span data-qa="cart-item__region">{product.regionTrl}</span>,
        },
    ].filter(Boolean) as PriceCartItemHHProps['list'];

    return <PriceCartItemHH product={product} list={list} title={<Title showPublicationsText={vpCount > 0} />} />;
};

export default memo(translation(DbAccess));
